'use client';

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { AppIcon } from '@impacta/shared';
import { PRESET_MESSAGES, MAX_MESSAGE_LENGTH } from '@/lib/gift-constants';

interface GiftWorkshopModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (attrs: Array<{ key: string; value: string }>) => Promise<void>;
  workshopTitle: string;
  sessionLabel: string;
  loading: boolean;
}

interface GiftWorkshopFormData {
  recipientName: string;
  recipientEmail: string;
  senderEmail: string;
  message: string;
}

const schema = yup.object({
  recipientName: yup.string().required('El nombre del destinatario es obligatorio'),
  recipientEmail: yup
    .string()
    .email('Email inválido')
    .required('El email del destinatario es obligatorio'),
  senderEmail: yup.string().email('Email inválido').required('Tu email es obligatorio'),
  message: yup.string().max(MAX_MESSAGE_LENGTH).default(''),
});

export default function GiftWorkshopModal({
  open,
  onClose,
  onSubmit,
  workshopTitle,
  sessionLabel,
  loading,
}: GiftWorkshopModalProps) {
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isValid },
  } = useForm<GiftWorkshopFormData>({
    mode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: {
      recipientName: '',
      recipientEmail: '',
      senderEmail: '',
      message: '',
    },
  });

  const message = watch('message');

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      reset();
      setSelectedPreset(null);
    }
  }, [open, reset]);

  // Lock body scroll when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [open]);

  function handlePresetClick(preset: string) {
    if (selectedPreset === preset) {
      setSelectedPreset(null);
      setValue('message', '');
    } else {
      setSelectedPreset(preset);
      setValue('message', preset);
    }
  }

  async function handleFormSubmit(data: GiftWorkshopFormData) {
    const attributes: Array<{ key: string; value: string }> = [
      { key: '_is_gift', value: 'true' },
      { key: '_recipient_name', value: data.recipientName.trim() },
      { key: '_recipient_email', value: data.recipientEmail.trim() },
      { key: '_sender_email', value: data.senderEmail.trim() },
      ...(data.message.trim() ? [{ key: '_gift_message', value: data.message.trim() }] : []),
    ];

    await onSubmit(attributes);
  }

  if (!open || !mounted) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[55] flex items-end md:items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="gift-workshop-title"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      {/* Modal / Bottom sheet */}
      <div className="relative w-full md:max-w-md md:mx-4 bg-white rounded-t-3xl md:rounded-3xl max-h-[90vh] overflow-y-auto animate-in slide-in-from-bottom duration-300">
        {/* Handle bar (mobile) */}
        <div className="md:hidden flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-[#E8E8E4]" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-4 pb-3 border-b border-[#E8E8E4]">
          <div className="flex items-center gap-2">
            <AppIcon name="GiftIcon" size={20} className="text-[#00A19B]" />
            <h2 id="gift-workshop-title" className="text-lg font-bold text-[#373A36]">
              Regalar esta actividad
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-[#F5F5F3] transition-colors"
            aria-label="Cerrar"
          >
            <AppIcon name="XMarkIcon" size={20} className="text-[#6b6e68]" />
          </button>
        </div>

        {/* Workshop info */}
        <div className="px-6 py-3 bg-[#FAFAF8] border-b border-[#E8E8E4]">
          <p className="text-sm font-semibold text-[#373A36] line-clamp-2">{workshopTitle}</p>
          <p className="text-xs text-[#6b6e68] mt-0.5">{sessionLabel}</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(handleFormSubmit)} className="px-6 py-5 space-y-4">
          {/* Recipient name */}
          <div>
            <label
              htmlFor="giftRecipientName"
              className="block text-sm font-medium text-[#373A36] mb-1 after:content-['_*'] after:text-red-500 after:font-bold"
            >
              Nombre del destinatario
            </label>
            <input
              id="giftRecipientName"
              type="text"
              {...register('recipientName')}
              className="w-full px-4 py-3 bg-[#FAFAF8] border border-[#E8E8E4] rounded-2xl text-[#373A36] text-sm focus:outline-none focus:ring-2 focus:ring-[#00A19B] focus:border-transparent transition-shadow"
              placeholder="Nombre de quién recibe el regalo"
            />
            {errors.recipientName && (
              <p className="mt-1 text-xs text-red-500">{errors.recipientName.message}</p>
            )}
          </div>

          {/* Recipient email */}
          <div>
            <label
              htmlFor="giftRecipientEmail"
              className="block text-sm font-medium text-[#373A36] mb-1 after:content-['_*'] after:text-red-500 after:font-bold"
            >
              Email del destinatario
            </label>
            <input
              id="giftRecipientEmail"
              type="email"
              {...register('recipientEmail')}
              className="w-full px-4 py-3 bg-[#FAFAF8] border border-[#E8E8E4] rounded-2xl text-[#373A36] text-sm focus:outline-none focus:ring-2 focus:ring-[#00A19B] focus:border-transparent transition-shadow"
              placeholder="email@ejemplo.com"
            />
            {errors.recipientEmail && (
              <p className="mt-1 text-xs text-red-500">{errors.recipientEmail.message}</p>
            )}
          </div>

          {/* Sender (buyer) details */}
          <div className="pt-2 border-t border-[#E8E8E4]">
            <p className="text-xs font-bold uppercase tracking-wide text-[#6b6e68] mb-2">
              Tus datos
            </p>
            <div className="space-y-3">
              <div>
                <label
                  htmlFor="giftSenderEmail"
                  className="block text-sm font-medium text-[#373A36] mb-1 after:content-['_*'] after:text-red-500 after:font-bold"
                >
                  Tu email
                </label>
                <input
                  id="giftSenderEmail"
                  type="email"
                  {...register('senderEmail')}
                  className="w-full px-4 py-3 bg-[#FAFAF8] border border-[#E8E8E4] rounded-2xl text-[#373A36] text-sm focus:outline-none focus:ring-2 focus:ring-[#00A19B] focus:border-transparent transition-shadow"
                  placeholder="tu@email.com"
                />
                {errors.senderEmail && (
                  <p className="mt-1 text-xs text-red-500">{errors.senderEmail.message}</p>
                )}
                <p className="mt-1 text-xs text-[#6b6e68]">
                  Te enviaremos el comprobante de compra a este email.
                </p>
              </div>
            </div>
          </div>

          {/* Personal message (optional) */}
          <div>
            <label htmlFor="giftMessage" className="block text-sm font-medium text-[#373A36] mb-1">
              Mensaje personal <span className="font-normal text-[#6b6e68]">(opcional)</span>
            </label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {PRESET_MESSAGES.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  aria-pressed={selectedPreset === preset}
                  onClick={() => handlePresetClick(preset)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 ${
                    selectedPreset === preset
                      ? 'bg-[#00A19B] text-white'
                      : 'bg-[#FAFAF8] text-[#6b6e68] border border-[#E8E8E4] hover:border-[#00A19B] hover:text-[#00A19B]'
                  }`}
                >
                  {preset}
                </button>
              ))}
            </div>
            <div className="relative">
              <textarea
                id="giftMessage"
                {...register('message')}
                onChange={(e) => {
                  setValue('message', e.target.value);
                  setSelectedPreset(null);
                }}
                maxLength={MAX_MESSAGE_LENGTH}
                rows={2}
                className="w-full px-4 py-3 bg-[#FAFAF8] border border-[#E8E8E4] rounded-2xl text-[#373A36] text-sm focus:outline-none focus:ring-2 focus:ring-[#00A19B] focus:border-transparent transition-shadow resize-none"
                placeholder="Escribe un mensaje personalizado..."
              />
              <span className="absolute bottom-2 right-3 text-xs text-[#6b6e68]">
                {message.length}/{MAX_MESSAGE_LENGTH}
              </span>
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={!isValid || loading}
            className="w-full py-3.5 rounded-full text-sm font-bold text-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90"
            style={{ background: '#00A19B' }}
          >
            <span className="flex items-center justify-center gap-2">
              <AppIcon name="GiftIcon" size={16} />
              {loading ? 'Agregando...' : 'Agregar regalo al carrito'}
            </span>
          </button>
        </form>
      </div>
    </div>,
    document.body
  );
}
